import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AiOutlinePhone, AiOutlineClose, AiOutlineAudioMuted, AiOutlineAudio, AiOutlineHistory, AiOutlineArrowLeft } from 'react-icons/ai';
import { FiPhoneIncoming, FiPhoneOutgoing, FiPhoneMissed } from 'react-icons/fi';
import myAxios from '../AxiosUrl';
import { CallAudio, Ringtone, preflightMic, micErrorText, micStatus } from './softphoneAudio';
import styles from './Softphone.module.css';

/**
 * Browser softphone (2026-10-31; full dialer 2026-11-04 following AI-Call-Center's FloatingDialer),
 * mounted once in App so a call survives page changes. Only for shops with an AI calling number
 * (GET /call/softphone/token → enabled). One WebSocket per tab to the calling service (control JSON
 * + call audio). It:
 *  - rings for customers calling the shop's number (Customer support mode "Softphone"), and when the
 *    AI support agent hands a caller over ("AI first, then a person");
 *  - dials from its keypad, from its call history, or from the order list:
 *    window.dispatchEvent(new CustomEvent('etech-softphone-dial', { detail: { mobile, orderId } }));
 *  - "Call from" picks which of the shop's numbers to use (when it has more than one), with a live dot;
 *  - ringback while an outgoing call rings; mute; timer; hang up;
 *  - lists the shop's other live calls (AI order / support calls) with "End";
 *  - history: All / Incoming / Outgoing / Missed, tap a row to call back.
 * Availability for other pages: window.__etechSoftphone = { enabled, online } + 'etech-softphone-status'.
 * Calls cost the softphone price per minute from the AI calling balance.
 */

const STATE_TEXT = { dialing: 'Calling…', ringing: 'Ringing…', answered: 'Connected', ended: 'Call ended' };
const KEYS = [
    ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
    ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
    ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
    ['*', ''], ['0', '+'], ['#', ''],
];
const PANEL_W = 320;
const PURPOSE_TEXT = { softphone: 'Person', support: 'AI support', test: 'Test call', campaign: 'AI call', order: 'AI order call', marketing: 'AI marketing' };
const KIND_TEXT = { incoming: 'Incoming', outgoing: 'Outgoing', missed: 'Missed' };

function publishStatus(enabled, online) {
    window.__etechSoftphone = { enabled, online };
    window.dispatchEvent(new CustomEvent('etech-softphone-status', { detail: { enabled, online } }));
}

const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};
const fmtWhen = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (d.toDateString() === today.toDateString()) return time;
    return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
};
/** Incoming that nobody talked on = missed. */
const kindOf = (c) => (c.direction === 'inbound' ? (c.durationSec > 0 ? 'incoming' : 'missed') : 'outgoing');
const canDrag = () => window.innerWidth > 600;

function Softphone() {
    const [enabled, setEnabled] = useState(false);
    const [online, setOnline] = useState(false);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState('dial'); // keypad | history
    const [number, setNumber] = useState('');
    const [incoming, setIncoming] = useState([]); // [{callId, from, kind, reason}]
    const [active, setActive] = useState(null); // {callId, mobile, state, answeredAt, handover}
    const [muted, setMuted] = useState(false);
    const [error, setError] = useState('');
    const [mic, setMic] = useState(null); // { permission, inputs, supported } — null until checked
    const [micBusy, setMicBusy] = useState(false);
    const [lines, setLines] = useState([]); // the shop's numbers [{numberId, number, registered, channels, inUse}]
    const [fromId, setFromId] = useState(''); // '' = any free number
    const [others, setOthers] = useState([]); // the shop's other live calls
    const [history, setHistory] = useState([]);
    const [histFilter, setHistFilter] = useState('all');
    const [histLoading, setHistLoading] = useState(false);
    const [pos, setPos] = useState(null); // {x, y} once dragged; null = docked bottom-right
    const [, setTick] = useState(0);

    const wsRef = useRef(null);
    const audioRef = useRef(null);
    const ringRef = useRef(new Ringtone());
    const pendingDialRef = useRef(null); // {mobile} while waiting for dial_result
    const retryRef = useRef(0);
    const closedRef = useRef(false);
    const dragRef = useRef(null);

    const send = useCallback((msg) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    }, []);

    const stopAudio = useCallback(() => {
        if (audioRef.current) audioRef.current.stop();
        audioRef.current = null;
        setMuted(false);
    }, []);

    const startAudio = useCallback(async () => {
        stopAudio();
        const audio = new CallAudio((frame) => {
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) ws.send(frame);
        });
        audioRef.current = audio;
        await audio.start();
    }, [stopAudio]);

    // ---- connection
    const connect = useCallback(async () => {
        if (closedRef.current) return;
        let info;
        try {
            const res = await myAxios.get('call/softphone/token');
            info = res.data;
        } catch (e) {
            info = null;
        }
        if (!info?.enabled) {
            setEnabled(false);
            publishStatus(false, false);
            return; // no number: no softphone
        }
        setEnabled(true);
        const ws = new WebSocket(`${info.wsUrl}?token=${encodeURIComponent(info.token)}`);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;
        ws.onopen = () => {
            retryRef.current = 0;
        };
        ws.onmessage = (e) => {
            if (typeof e.data !== 'string') {
                audioRef.current && audioRef.current.play(new Uint8Array(e.data));
                return;
            }
            let msg;
            try {
                msg = JSON.parse(e.data);
            } catch (err) {
                return;
            }
            if (msg.type === 'hello') {
                setOnline(true);
                publishStatus(true, true);
                ws.send(JSON.stringify({ type: 'lines' }));
            } else if (msg.type === 'incoming') {
                setIncoming((list) => (list.some((x) => x.callId === msg.callId) ? list : [...list, msg]));
            } else if (msg.type === 'incoming_gone') {
                setIncoming((list) => list.filter((x) => x.callId !== msg.callId));
            } else if (msg.type === 'dial_result') {
                const pending = pendingDialRef.current;
                pendingDialRef.current = null;
                if (msg.ok) {
                    setActive({ callId: msg.callId, mobile: pending?.mobile || '', state: 'dialing', answeredAt: null });
                } else {
                    stopAudio();
                    setActive(null);
                    setError(msg.error || 'The call could not be started');
                }
            } else if (msg.type === 'state') {
                setActive((cur) => {
                    if (!cur || cur.callId !== msg.callId) {
                        // a handed-over AI call this tab answered
                        if (msg.handover) return { callId: msg.callId, mobile: '', state: 'answered', answeredAt: Date.now(), handover: true };
                        return cur;
                    }
                    if (msg.state === 'ended') return { ...cur, state: 'ended', reason: msg.reason || '' };
                    return { ...cur, state: msg.state, answeredAt: msg.state === 'answered' ? cur.answeredAt || Date.now() : cur.answeredAt };
                });
                if (msg.state === 'ended') {
                    stopAudio();
                    setTimeout(() => setActive((cur) => (cur && cur.callId === msg.callId && cur.state === 'ended' ? null : cur)), 3000);
                }
            } else if (msg.type === 'lines') {
                setLines(Array.isArray(msg.lines) ? msg.lines : []);
            } else if (msg.type === 'active') {
                const mine = msg.mine || null;
                setOthers((Array.isArray(msg.calls) ? msg.calls : []).filter((c) => c.callId !== mine));
            } else if (msg.type === 'end_result') {
                if (!msg.ok) setError('That call has already ended');
                setOthers((list) => list.filter((c) => c.callId !== msg.callId));
            } else if (msg.type === 'error') {
                setError(msg.message || 'Softphone error');
            }
        };
        ws.onclose = () => {
            wsRef.current = null;
            setOnline(false);
            publishStatus(true, false);
            setIncoming([]);
            setOthers([]);
            stopAudio();
            setActive((cur) => (cur && cur.state !== 'ended' ? { ...cur, state: 'ended', reason: 'connection lost' } : cur));
            if (closedRef.current) return;
            const wait = Math.min(30000, 2000 * 2 ** retryRef.current++);
            setTimeout(connect, wait);
        };
    }, [stopAudio]);

    useEffect(() => {
        closedRef.current = false;
        connect();
        const ring = ringRef.current;
        return () => {
            closedRef.current = true;
            ring.stop();
            if (wsRef.current) wsRef.current.close();
            if (audioRef.current) audioRef.current.stop();
            publishStatus(false, false);
        };
    }, [connect]);

    const inCall = !!(active && active.state !== 'ended');

    // tones: incoming ringtone first; ringback while my outgoing call is being set up / rings
    useEffect(() => {
        const ring = ringRef.current;
        if (incoming.length && !inCall) ring.start('ringtone');
        else if (active && !active.handover && (active.state === 'dialing' || active.state === 'ringing')) ring.start('ringback');
        else ring.stop();
    }, [incoming, active, inCall]);

    // an incoming call opens the panel on the keypad screen
    useEffect(() => {
        if (incoming.length) setOpen(true);
    }, [incoming.length]);

    // call timer
    useEffect(() => {
        if (!active || active.state !== 'answered') return undefined;
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, [active]);

    // while the panel is open: the shop's numbers + live calls every few seconds
    useEffect(() => {
        if (!open || !online) return undefined;
        const poll = () => {
            send({ type: 'lines' });
            send({ type: 'active' });
        };
        poll();
        const t = setInterval(poll, 4000);
        return () => clearInterval(t);
    }, [open, online, send]);

    // keep "Call from" on a number that still exists
    useEffect(() => {
        if (fromId && !lines.some((l) => l.numberId === fromId)) setFromId('');
    }, [lines, fromId]);

    // ---- history (the same list as AI calling → Calls), refreshed when it opens and after a call
    const loadHistory = useCallback(async () => {
        setHistLoading(true);
        try {
            const res = await myAxios.get('call/calls', { params: { page: 1, limit: 40 } });
            setHistory(Array.isArray(res.data?.calls) ? res.data.calls : []);
        } catch (e) {
            /* keep the last list */
        } finally {
            setHistLoading(false);
        }
    }, []);
    useEffect(() => {
        if (open && view === 'history') loadHistory();
    }, [open, view, loadHistory]);
    useEffect(() => {
        if (active?.state === 'ended' && open && view === 'history') loadHistory();
    }, [active?.state, open, view, loadHistory]);

    const histRows = useMemo(() => {
        const rows = history.filter((c) => c.mobile && (histFilter === 'all' || kindOf(c) === histFilter));
        return rows.slice(0, 25);
    }, [history, histFilter]);
    const histCount = useMemo(() => {
        const n = { incoming: 0, outgoing: 0, missed: 0 };
        history.forEach((c) => c.mobile && (n[kindOf(c)] += 1));
        return n;
    }, [history]);

    // ---- microphone: checked when the panel opens and when a device is plugged in / out
    const checkMic = useCallback(async () => {
        const st = await micStatus();
        setMic(st);
        return st;
    }, []);
    useEffect(() => {
        if (!open) return undefined;
        checkMic();
        const md = navigator.mediaDevices;
        if (!md?.addEventListener) return undefined;
        md.addEventListener('devicechange', checkMic);
        return () => md.removeEventListener('devicechange', checkMic);
    }, [open, checkMic]);
    /** Ask for the microphone now (the browser's Allow prompt appears when it can see a microphone). */
    const allowMic = useCallback(async () => {
        setMicBusy(true);
        setError('');
        try {
            await preflightMic();
            await checkMic();
        } catch (e) {
            setError(micErrorText(e, await checkMic()));
        } finally {
            setMicBusy(false);
        }
    }, [checkMic]);
    const micReady = mic && mic.permission === 'granted' && mic.inputs !== 0;

    // ---- drag (desktop): grab the header
    useEffect(() => {
        const move = (e) => {
            const d = dragRef.current;
            if (!d) return;
            const w = Math.min(PANEL_W, window.innerWidth - 16);
            setPos({
                x: Math.max(8, Math.min(d.ox + e.clientX - d.sx, window.innerWidth - w - 8)),
                y: Math.max(8, Math.min(d.oy + e.clientY - d.sy, window.innerHeight - 120)),
            });
        };
        const up = () => {
            dragRef.current = null;
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        const clamp = () => {
            if (!canDrag()) return setPos(null);
            setPos((p) => (p ? { x: Math.max(8, Math.min(p.x, window.innerWidth - PANEL_W - 8)), y: Math.max(8, Math.min(p.y, window.innerHeight - 120)) } : p));
        };
        window.addEventListener('resize', clamp);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('resize', clamp);
        };
    }, []);
    const startDrag = (e) => {
        if (!canDrag() || e.target.closest('button')) return;
        const box = e.currentTarget.parentElement.getBoundingClientRect();
        dragRef.current = { sx: e.clientX, sy: e.clientY, ox: box.left, oy: box.top };
    };
    const panelStyle = pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;

    // ---- actions
    const dial = useCallback(
        async (mobile, orderId = null) => {
            setError('');
            const m = String(mobile || '').replace(/[^\d+]/g, '');
            if (!/^(\+?880)?01[3-9]\d{8}$/.test(m)) {
                setOpen(true);
                setView('dial');
                setError('Enter a Bangladeshi mobile number (01XXXXXXXXX)');
                return;
            }
            if (!online) {
                setOpen(true);
                setError('The softphone is not connected — wait a moment and try again');
                return;
            }
            if (active && active.state !== 'ended') {
                setOpen(true);
                setError('Finish the current call first');
                return;
            }
            // ringback starts inside the click, so the browser lets it play
            ringRef.current.start('ringback');
            try {
                await startAudio();
            } catch (e) {
                ringRef.current.stop();
                stopAudio();
                setOpen(true);
                setError(micErrorText(e, await micStatus()));
                return;
            }
            pendingDialRef.current = { mobile: m };
            setActive({ callId: null, mobile: m, state: 'dialing', answeredAt: null });
            setOpen(true);
            setView('dial');
            setNumber('');
            send({ type: 'dial', mobile: m, orderId, numberId: fromId || null });
        },
        [online, active, startAudio, stopAudio, send, fromId]
    );

    const answer = async (call) => {
        setError('');
        try {
            await preflightMic();
            await startAudio();
        } catch (e) {
            stopAudio();
            setError(micErrorText(e, await micStatus()));
            return;
        }
        setIncoming((list) => list.filter((x) => x.callId !== call.callId));
        setActive({ callId: call.callId, mobile: call.from, state: 'answered', answeredAt: Date.now(), handover: call.kind === 'transfer' });
        setOpen(true);
        send({ type: 'answer', callId: call.callId });
    };

    const decline = (call) => {
        setIncoming((list) => list.filter((x) => x.callId !== call.callId));
        send({ type: 'decline', callId: call.callId });
    };

    const hangup = () => {
        send({ type: 'hangup' });
        stopAudio();
        setActive((cur) => (cur ? { ...cur, state: 'ended' } : cur));
        setTimeout(() => setActive((cur) => (cur && cur.state === 'ended' ? null : cur)), 2000);
    };

    const endOther = (callId) => {
        if (!window.confirm('End this call?')) return;
        send({ type: 'end', callId });
    };

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        audioRef.current && audioRef.current.setMuted(next);
    };

    const press = (d) => {
        setError('');
        setNumber((n) => (n + d).slice(0, 20));
    };
    const backspace = () => setNumber((n) => n.slice(0, -1));
    const fromHistory = (c) => {
        setNumber(c.mobile);
        setError('');
        setView('dial');
    };

    // order list / other pages ask to dial
    useEffect(() => {
        const onDial = (e) => dial(e.detail?.mobile, e.detail?.orderId || null);
        window.addEventListener('etech-softphone-dial', onDial);
        return () => window.removeEventListener('etech-softphone-dial', onDial);
    }, [dial]);

    if (!enabled) return null;
    const seconds = active?.answeredAt ? Math.floor((Date.now() - active.answeredAt) / 1000) : 0;
    const fromLine = lines.find((l) => l.numberId === fromId);
    const dotFor = (l) => (!l ? (lines.some((x) => x.registered) ? styles.dotOn : styles.dotOff) : !l.registered ? styles.dotOff : l.inUse >= l.channels ? styles.dotBusy : styles.dotOn);
    const lineTitle = (l) => (!l.registered ? 'not connected' : l.inUse >= l.channels ? 'all channels busy' : 'ready');

    return (
        <>
            {incoming.length > 0 && !inCall && (
                <div className={styles.incoming} role="alertdialog" aria-label="Incoming call">
                    {incoming.slice(0, 1).map((c) => (
                        <div key={c.callId}>
                            <div className={styles.incomingTitle}>{c.kind === 'transfer' ? 'The AI is passing you a caller' : 'Customer calling'}</div>
                            <div className={styles.incomingNumber}>{c.from}</div>
                            {c.reason ? <div className={styles.incomingReason}>{c.reason}</div> : null}
                            <div className={styles.incomingActions}>
                                <button className={styles.btnAnswer} onClick={() => answer(c)}>
                                    <AiOutlinePhone /> Answer
                                </button>
                                <button className={styles.btnDecline} onClick={() => decline(c)}>
                                    Decline
                                </button>
                            </div>
                        </div>
                    ))}
                    {incoming.length > 1 && <div className={styles.incomingReason}>+{incoming.length - 1} more calling</div>}
                    {error && <div className={styles.error}>{error}</div>}
                </div>
            )}

            {open && (
                <div className={styles.panel} style={panelStyle}>
                    <div className={`${styles.panelHeader} ${canDrag() ? styles.draggable : ''}`} onPointerDown={startDrag}>
                        <span>
                            <span className={`${styles.dot} ${online ? styles.dotOn : styles.dotOff}`} /> Softphone {online ? '' : '· connecting…'}
                        </span>
                        <button className={styles.iconBtn} onClick={() => setOpen(false)} aria-label="Close">
                            <AiOutlineClose />
                        </button>
                    </div>

                    {active ? (
                        <div className={styles.call}>
                            <div className={styles.callLabel}>{active.handover ? 'Taken over from the AI' : active.state === 'answered' ? 'On call' : 'Outgoing call'}</div>
                            <div className={styles.callNumber}>{active.mobile || 'Customer'}</div>
                            <div className={`${styles.callState} ${active.state === 'answered' ? styles.callLive : ''}`}>
                                {active.state === 'answered' ? fmt(seconds) : STATE_TEXT[active.state] || active.state}
                                {active.state === 'ended' && active.reason ? ` (${active.reason.replace(/_/g, ' ')})` : ''}
                            </div>
                            {inCall && (
                                <div className={styles.callActions}>
                                    <button className={`${styles.btnRound} ${muted ? styles.btnRoundOn : ''}`} onClick={toggleMute} disabled={active.state !== 'answered'} aria-label={muted ? 'Unmute' : 'Mute'} title={muted ? 'Unmute' : 'Mute'}>
                                        {muted ? <AiOutlineAudioMuted /> : <AiOutlineAudio />}
                                    </button>
                                    <button className={styles.btnHangup} onClick={hangup}>
                                        Hang up
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : view === 'history' ? (
                        <div className={styles.history}>
                            <div className={styles.paneHead}>
                                <button className={styles.iconBtn} onClick={() => setView('dial')} aria-label="Back to keypad">
                                    <AiOutlineArrowLeft />
                                </button>
                                <span className={styles.paneTitle}>
                                    <AiOutlineHistory /> Call history
                                </span>
                                <span style={{ width: 24 }} />
                            </div>
                            <div className={styles.chips} role="tablist">
                                {['all', 'incoming', 'outgoing', 'missed'].map((k) => (
                                    <button key={k} type="button" role="tab" aria-selected={histFilter === k} className={`${styles.chip} ${histFilter === k ? styles.chipOn : ''} ${k === 'missed' ? styles.chipMissed : ''}`} onClick={() => setHistFilter(k)}>
                                        {k === 'all' ? 'All' : KIND_TEXT[k]}
                                        {k !== 'all' && <span className={styles.chipCount}>{histCount[k]}</span>}
                                    </button>
                                ))}
                            </div>
                            {histLoading && !history.length ? (
                                <div className={styles.empty}>Loading…</div>
                            ) : histRows.length === 0 ? (
                                <div className={styles.empty}>No calls yet</div>
                            ) : (
                                <div className={styles.histList}>
                                    {histRows.map((c) => {
                                        const k = kindOf(c);
                                        return (
                                            <button key={c._id} type="button" className={styles.histRow} onClick={() => fromHistory(c)} title="Tap to call back">
                                                <span className={`${styles.histIcon} ${styles['hist_' + k]}`}>
                                                    {k === 'missed' ? <FiPhoneMissed /> : k === 'incoming' ? <FiPhoneIncoming /> : <FiPhoneOutgoing />}
                                                </span>
                                                <span className={styles.histInfo}>
                                                    <span className={styles.histNum}>{c.mobile}</span>
                                                    <span className={styles.histMeta}>
                                                        <span className={k === 'missed' ? styles.histMissedText : ''}>{KIND_TEXT[k]}</span>
                                                        {c.kind && c.kind !== 'softphone' ? ` · AI ${c.kindText || c.kind}` : ''}
                                                        {c.durationSec > 0 ? ` · ${fmt(c.durationSec)}` : ''} · {fmtWhen(c.at)}
                                                    </span>
                                                </span>
                                                <AiOutlinePhone className={styles.histCall} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <Link to="/ai-calling?tab=calls" className={styles.histAll} onClick={() => setOpen(false)}>
                                All calls, recordings and transcripts →
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.dialer}>
                            {mic && !micReady && (
                                <div className={`${styles.micLine} ${styles.micWarn}`}>
                                    <span>🎙 {mic.inputs === 0 ? 'No microphone found' : mic.permission === 'denied' ? 'Microphone blocked for this site' : 'Microphone not allowed yet'}</span>
                                    <button className={styles.btnMic} onClick={allowMic} disabled={micBusy}>
                                        {micBusy ? 'Asking…' : mic.inputs === 0 ? 'Check again' : 'Allow microphone'}
                                    </button>
                                </div>
                            )}

                            <div className={styles.dialTop}>
                                <label className={styles.fromBox} title={fromLine ? `${fromLine.number} · ${lineTitle(fromLine)}` : 'Call from any free number'}>
                                    <span className={`${styles.dot} ${dotFor(fromLine)}`} />
                                    {lines.length > 1 ? (
                                        <select className={styles.fromSelect} value={fromId} onChange={(e) => setFromId(e.target.value)} aria-label="Call from">
                                            <option value="">Any free number</option>
                                            {lines.map((l) => (
                                                <option key={l.numberId} value={l.numberId}>
                                                    {l.number} — {lineTitle(l)}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className={styles.fromOne}>{lines[0] ? `From ${lines[0].number}` : 'Your number'}</span>
                                    )}
                                </label>
                                <button className={styles.tabBtn} onClick={() => setView('history')} title="Call history">
                                    <AiOutlineHistory /> History
                                </button>
                            </div>

                            <div className={styles.display}>
                                <input
                                    className={styles.input}
                                    inputMode="tel"
                                    placeholder="01XXXXXXXXX"
                                    value={number}
                                    onChange={(e) => setNumber(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && dial(number)}
                                    aria-label="Number to call"
                                />
                                {number && (
                                    <button className={styles.bksp} onClick={backspace} aria-label="Delete last digit">
                                        ⌫
                                    </button>
                                )}
                            </div>

                            <div className={styles.keypad}>
                                {KEYS.map(([d, s]) => (
                                    <button key={d} type="button" className={styles.key} onClick={() => press(d)}>
                                        <span>{d}</span>
                                        <small>{s || ' '}</small>
                                    </button>
                                ))}
                            </div>

                            <button className={styles.btnCall} onClick={() => dial(number)} disabled={!online || !number}>
                                <AiOutlinePhone /> Call
                            </button>
                            <div className={styles.hint}>Calls go out from your shop's number. Paid per minute from your AI calling balance.</div>
                        </div>
                    )}

                    {others.length > 0 && (
                        <div className={styles.others}>
                            <div className={styles.othersHead}>Other live calls on your numbers</div>
                            {others.map((c) => (
                                <div key={c.callId} className={styles.otherRow}>
                                    <span className={`${styles.dot} ${c.state === 'answered' || c.state === 'active' ? styles.dotOn : styles.dotBusy}`} />
                                    <span className={styles.histInfo}>
                                        <span className={styles.histNum}>{c.mobile || '—'}</span>
                                        <span className={styles.histMeta}>
                                            {PURPOSE_TEXT[c.purpose] || 'AI call'} · {c.state === 'answered' || c.state === 'active' ? 'talking' : c.state}
                                        </span>
                                    </span>
                                    <button className={styles.btnEnd} onClick={() => endOther(c.callId)} title="End this call">
                                        End
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && <div className={styles.error}>{error}</div>}
                </div>
            )}

            <button
                className={`${styles.fab} ${inCall ? styles.fabCall : ''} ${incoming.length && !inCall ? styles.fabRing : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-label="Softphone"
                title={online ? 'Softphone' : 'Softphone (connecting…)'}
            >
                <AiOutlinePhone />
                <span className={`${styles.fabDot} ${online ? styles.dotOn : styles.dotOff}`} />
                {incoming.length > 0 && !inCall && <span className={styles.fabBadge}>{incoming.length}</span>}
            </button>
        </>
    );
}

/**
 * Mounted ONCE in App (inside the router, outside the routes) so a call survives page changes.
 * Re-checks the login on every navigation; a different login remounts it (key).
 */
export function SoftphoneGate() {
    useLocation();
    let token = null;
    try {
        token = localStorage.getItem('token');
    } catch (e) {
        token = null;
    }
    if (!token) return null;
    return <Softphone key={token} />;
}

export default Softphone;
